import {Delete} from '@mui/icons-material';
import {useCan} from 'src/permissions/hooks';
import {IconButton} from '@mui/material';
import {FC} from 'react';
import {chargersApi, companyApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import {ChargerSortFieldEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ChargePointDTO} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import theme from 'src/theme';

type RefetchChargerTrigger = ReturnType<typeof chargersApi.useGetChargersQuery>[0];
type DeleteChargerMutationTrigger = ReturnType<typeof chargersApi.useDeleteChargePointMutation>[0];
type DeleteChargerForCompanyMutationTrigger = ReturnType<
    typeof companyApi.useRemoveChargerFromCompanyMutation
>[0];

const handleDeleteCharger =
    (
        trigger: DeleteChargerMutationTrigger,
        companyTrigger: DeleteChargerForCompanyMutationTrigger,
        refetch: RefetchChargerTrigger,
        dispatch: AppDispatch,
        businessId: number,
        chargerId: number,
        companyId?: number,
        isCompanyUser: boolean = false
    ) =>
        async () => {
            dispatch(preloaderChangeStatus(true));
            try {
                if (isCompanyUser) {
                    await companyTrigger({
                        companyId: companyId ?? 0,
                        data: {
                            businessId,
                            chargerId,
                        },
                    }).unwrap();

                    refetch();

                    return;
                }

                await trigger({businessId, chargerId}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const DeleteCharger: FC<{ row: ChargePointDTO; isCompanyUser: boolean }> = ({
                                                                                       row,
                                                                                       isCompanyUser = false,
                                                                                   }) => {
    // The control is the action: without the permission the server refuses it, so showing it
    // offers a button whose only outcome is an error.
    // One icon, two actions. A company user detaches the charger from their company; everyone else
    // deletes it outright, and those are different permissions. Asking only for the first showed the
    // bin to people whose click came back 403.
    const canUnlinkFromCompany = useCan('companies.assignChargers');
    const canDeleteCharger = useCan('chargers.delete');
    const canDoIt = isCompanyUser ? canUnlinkFromCompany : canDeleteCharger;
    const [trigger] = chargersApi.useDeleteChargePointMutation();
    const [companyTrigger] = companyApi.useRemoveChargerFromCompanyMutation();
    const {refetch} = chargersApi.useGetChargersQuery({
        data: {
            businessId: row.business.id,
            companyId: row?.company?.id,
            sortField: ChargerSortFieldEnum.CREATED_AT,
        },
        params: {
            page: 0,
            size: 100,
        },
    });

    const dispatch = useAppDispatch();

    if (!canDoIt) {
        return null;
    }

    return (
        <>
            <IconButton
                sx={{
                    width: '28px',
                    height: '28px',
                    p: '2px',
                    color: theme.palette.neutral[400],
                    backgroundColor: 'transparent',
                    '.MuiSvgIcon-root': {
                        fill: theme.palette.neutral[400],
                        width: '20px',
                    },
                }}
                onClick={handleDeleteCharger(
                    trigger,
                    companyTrigger,
                    refetch,
                    dispatch,
                    row.business.id,
                    row.id,
                    row?.company?.id,
                    isCompanyUser
                )}
            >
                <Delete/>
            </IconButton>
        </>
    );
};
